import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';

import {
  Client, FileParameter, FileType,
  GuidDictionaryItem,
  MeterAutentification,
  MeterInterface,
  MeterPhase,
  MeterModelDataIn,
  MeterSecurity,
  PermissionsEnum,
} from '../../../../../api/api-reference';
import {AuthService} from "../../../../../services/auth.service";
import { ConfirmDialogComponent } from '../../../../core/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-meter-model-setup',
  standalone: true,
  imports: [TranslateModule, CommonModule, FormsModule, AngularSvgIconModule, ConfirmDialogComponent],
  templateUrl: './meter-model-setup.component.html',
  styleUrl: './meter-model-setup.component.scss',
})
export class MeterModelSetupComponent implements OnInit {
  @Input() selectedMeterModel: string | null = null;
  @Output() closeModal = new EventEmitter<boolean>();
  data: MeterModelDataIn = new MeterModelDataIn();
  creationDate: Date | undefined;
  metermakes: GuidDictionaryItem[] = [];
  public PermissionEnum = PermissionsEnum;
  public fileParameter: FileParameter | undefined;
  disconnectControlModes: GuidDictionaryItem[] = [];
  originalDisconnectControlMode: string | undefined;
  isDisconnectConfirmOpen = false;

  @ViewChild('crudform') crudform!: NgForm;

  constructor(
    private client: Client,
    private toastr: ToastrService,
    public authService: AuthService,
  ) {
  }

  ngOnInit(): void {
    this.getMeterModel();
    this.getMeterMakes();
    this.getDisconnectControlModes();
  }

  meterPhaseDictionary: MeterPhase[] = Object.keys(MeterPhase)
    .filter((key) => isNaN(Number(key)))
    .map((key) => MeterPhase[key as keyof typeof MeterPhase]);

  meterInterfaceDictionary: MeterInterface[] = Object.keys(MeterInterface)
    .filter((key) => isNaN(Number(key)))
    .map((key) => MeterInterface[key as keyof typeof MeterInterface]);

  autentificationTypeDictionary: MeterAutentification[] = Object.keys(
    MeterAutentification,
  )
    .filter((key) => isNaN(Number(key)))
    .map(
      (key) => MeterAutentification[key as keyof typeof MeterAutentification],
    );

  securityDictionary: MeterSecurity[] = Object.keys(MeterSecurity)
    .filter((key) => isNaN(Number(key)))
    .map((key) => MeterSecurity[key as keyof typeof MeterSecurity]);

  getMeterModel() {
    if (this.selectedMeterModel) {
      this.client.getMeterModel(this.selectedMeterModel).subscribe({
        next: (response) => {
          this.data.id = response.id;
          this.data.firmwareVersion = response.firmwareVersion ?? '';
          this.data.model = response.model ?? '';
          this.data.meterMake = response.meterMakeId ?? '';
          this.data.code = response.code ?? '';
          this.data.authorizationLevel = response.authorizationLevel!;
          this.data.clientAddress = response.clientAddress!;
          this.data.interface = response.interface!;
          this.data.security = response.security!;
          this.data.smsRetryTimeout = response.smsRetryTimeout!;
          this.data.smsWakeTimeout = response.smsWakeTimeout!;
          this.data.smsWakeMessage = response.smsWakeMessage!;
          this.data.systemTitle = response.systemTitle!;
          this.data.firmwareImage = response.firmwareImage!;
          this.data.firmwareIdentifier = response.firmwareIdentifier!;
          this.data.disconnectControlMode = response.dlmsDisconnectControlMode!;
          this.originalDisconnectControlMode = this.data.disconnectControlMode;
          this.data.meterPhase = response.meterPhase!;
          this.creationDate = response.creationDate;
        },
        error: (err) => {
          this.toastr.error(err);
        },
      });
    }
  }

  saveMeterModelData(){
    this.client.saveMeterModel(this.data).subscribe({
      next: (response) => {
        this.toastr.success(response);
        this.toggleModal(true);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  get firmwareIdentifierMissing(): boolean {
    return (!!this.fileParameter || !!this.data.firmwareImage) && !this.data.firmwareIdentifier?.trim();
  }

  onSubmit() {
    // 🚫 Block submit if firmware file exists but identifier is missing
    if (this.fileParameter && !this.data.firmwareIdentifier) {
      return;
    }

    if (this.isEditMode && this.data.disconnectControlMode !== this.originalDisconnectControlMode) {
      this.isDisconnectConfirmOpen = true;
      return;
    }

    this.submitMeterModel();
  }

  confirmDisconnectChange() {
    this.isDisconnectConfirmOpen = false;
    this.submitMeterModel();
  }

  cancelDisconnectChange() {
    this.isDisconnectConfirmOpen = false;
  }

  private submitMeterModel() {
    if(this.fileParameter){
      this.client.uploadFirmwareFile(FileType.FirmwareBinaryImport, this.fileParameter).subscribe({
        next: (response) => {
          this.data.firmwareImage = response;
          this.saveMeterModelData();
        },
        error: (err) => {
          this.toastr.error(err);
        },
      });
    }else{
      this.saveMeterModelData();
    }
  }

  getMeterMakes() {
    this.client.getAllMeterMakersForOptions().subscribe({
      next: (response) => {
        this.metermakes = response ?? [];
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  getDisconnectControlModes() {
    this.client.getAllDisconnectControlModeForOptions().subscribe({
      next: (response) => {
        this.disconnectControlModes = response ?? [];
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  toggleModal(reaload?: boolean) {
    this.closeModal.emit(reaload);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input?.files?.length) {
      const file = input.files[0];
      if (!file.name.toLowerCase().endsWith('.bin') && !file.name.toLowerCase().endsWith('.hex')) {
        this.toastr.error('Please select a valid BIN or HEX file.');
        return;
      }
      this.fileParameter = {
        data: file,
        fileName: file.name
      }
    }
  }

  get isEditMode(): boolean {
    return !!this.data?.id;
  }
}
